import { faInfoCircle, faLayerGroup } from "@fortawesome/pro-solid-svg-icons"
import { FilterList, Save } from "@mui/icons-material"
import {
  Box,
  Button,
  Grid,
  IconButton,
  Skeleton,
  TableHead,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableRow from "@mui/material/TableRow"
import { useApi } from "auth/useApi3"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable
} from "@tanstack/react-table"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { AppContext } from "components/AppContextProvider"
import DefaultButton from "components/Button"
import NoResultFound from "components/Dialogs/NoResultFound"
import theme from "css/theme/light"
import { apiEndpoints } from "generated/apiEndpoints"
import { t } from "i18next"
import moment from "moment"
import { deviationStatusStrings } from "pages/deviation/pages/helpers/deviationStatus"
import { prio, statusString } from "pages/issuereports/pages/archive/components/table/helpers"
import { useHistory } from "react-router-dom"
import { StyledBox, StyledFilterContainer, StyledTable, StyledTableFooter } from "../ClpTable.styled"
import { ClpTableEnum } from "../ClpTableEnum"
import { DefaultColumnFilter, GlobalFilter } from "../ClpTableFilterTypes"
import { registerTable } from "../columnInspector"
import ClpTableRow from "../components/ClpTableRow"
import ExportToExcel from "../components/ExportToExcel2"
import { StyledFilterGrid } from "../components/FilterGrid.styled"
import MoreFilters from "../components/MoreFilters"
import Pagination from "../components/Pagination"
import PrintTableToPdt from "../components/PrintToPdf"
import RadioButtons from "../components/RadioButtons"
import SelectedFilters from "../components/SelectedFilters"
import TableHeader from "../components/TableHeader"
import {
  ClpTableColumnFilterDto,
  ClpTablePaginationFilter,
  ServerSidePagingTableProps,
  TableLoadingStateEnum
} from "../tableProps.interface"

interface UpsertFiltersDto {
  clpTableId: ClpTableEnum
  columnFilters: ColumnFiltersState
  globalFilter: string
}

const emptyState = {
  pageSize: 25,
  pageIndex: 0,
  expanded: {},
  sortBy: [],
  filters: [],
  hiddenColumns: []
}

const ClpTableServerSidePaging = <T extends object>({
  columns,
  subColumns,
  initialFilters,
  hideSearch,
  hideFilter,
  onRowClick,
  print,
  footer,
  tableId,
  pagedDataApiEndpoint,
  nonPagedDataApiEndpoint,
  refreshKey,
  saveFilterTooltipText
}: ServerSidePagingTableProps<T>) => {
  const appContext = useContext(AppContext)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialFilters ? initialFilters : [])
  const [pagination, setPagination] = React.useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: 25
  }))
  const [sort, setSort] = useState<{ id: string; desc: boolean } | null>(null)
  const [filtersOpen, expandFilters] = useState(false)
  const [globalFilter, setGlobalFilter] = React.useState("")
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"))
  const [saveFiltersApiResult, saveFiltersApi] = useApi() as [IApiResult, IApiFunc]
  const [getSavedFiltersApiResult, getSavedFiltersApi] = useApi() as [IApiResult, IApiFunc]
  const [pagedResult, pagedApi] = useApi() as [IApiResult, IApiFunc]
  const [nonPagedResult, nonPagedApi] = useApi() as [IApiResult, IApiFunc]
  const [rows, setRows] = useState<T[]>([])
  const firstLoadRef = useRef(true)

  const tableColumns = React.useMemo(() => {
    if (pagedResult.status === TableLoadingStateEnum.Loading) {
      return columns.map((column: ColumnDef<T>) => ({
        ...column,
        cell: () => <Skeleton />,
        meta: {
          ...column.meta
        }
      }))
    }

    return columns.map((col) => {
      const c = { ...col } as ColumnDef<T, any> & { accessorKey?: string; id?: string }
      if (!c.id && c.accessorKey) {
        c.id = c.accessorKey
      }
      return c
    })
  }, [pagedResult.status, columns])

  //Used for inspecting table columns ids and accessorkeys
  useEffect(() => {
    registerTable(tableColumns)
  }, [tableColumns])

  //#region Table data fetch & triggers
  const fetchPagedServerData = () => {
    const mappedFilters: ClpTableColumnFilterDto[] = columnFilters.map((filter) => ({
      id: filter.id,
      value: Array.isArray(filter.value) ? filter.value.map((val) => String(val)) : [String(filter.value)]
    }))

    mappedFilters.push({
      id: "global",
      value: [globalFilter]
    })

    const requestBody: ClpTablePaginationFilter = {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortBy: sort?.id || "",
      sortDescending: sort?.desc || false,
      filters: mappedFilters
    }

    pagedApi.post(pagedDataApiEndpoint, requestBody)
  }

  const fetchFilteredNonPagedServerData = () => {
    const mappedFilters: ClpTableColumnFilterDto[] = columnFilters.map((filter) => ({
      id: filter.id,
      value: Array.isArray(filter.value) ? filter.value.map((val) => String(val)) : [String(filter.value)]
    }))

    mappedFilters.push({
      id: "global",
      value: [globalFilter]
    })

    nonPagedApi.post(nonPagedDataApiEndpoint, mappedFilters)
  }

  useEffect(() => {
    if (getSavedFiltersApiResult.status !== 2) return

    const timeout = setTimeout(() => {
      setPageIndex(0) //Reset pageIndex if fecth is not triggered by page change
      fetchPagedServerData()
      fetchFilteredNonPagedServerData()
    }, 1000) //1 second cooldown between fetches

    return () => clearTimeout(timeout)
  }, [globalFilter, columnFilters])

  useEffect(() => {
    if (getSavedFiltersApiResult.status !== 2) return
    //Reset pageIndex if fecth is not triggered by page change
    setPageIndex(0)
    fetchPagedServerData()
    //fetchFilteredNonPagedServerData()
    firstLoadRef.current = false
  }, [sort])

  useEffect(() => {
    if (getSavedFiltersApiResult.status !== 2) return
    fetchPagedServerData()
    //fetchFilteredNonPagedServerData()
  }, [pagination.pageIndex, pagination.pageSize, refreshKey])
  //#endregion

  useEffect(() => {
    if (pagedResult.status !== 2) return
    setRows(pagedResult.data.items)
  }, [pagedResult])

  //#region Filter saving and fetching
  function saveFilters() {
    if (tableId == null) return

    for (const filter of columnFilters) {
      const { id, value } = filter

      if (typeof value === "string") {
        filter.value = [value]
      }

      if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
        continue
      } else {
        console.error(`Filter "${id}" has invalid type. Expected string[] or string, got:`, value)
      }
    }

    const body: UpsertFiltersDto = {
      clpTableId: tableId,
      columnFilters: columnFilters.map((column) => ({
        ...column,
        value: Array.isArray(column.value) ? column.value.map((val) => String(val)) : column.value
      })),
      globalFilter: globalFilter
    }

    saveFiltersApi.put(apiEndpoints.clptableuserfilter.upsert, body)
  }

  function fetchSavedFilters() {
    if (tableId == null) return

    const url = `${apiEndpoints.clptableuserfilter.get}?clpTableId=${tableId}`
    getSavedFiltersApi.get(url)
  }

  useEffect(() => {
    fetchSavedFilters()
  }, [])

  useEffect(() => {
    if (getSavedFiltersApiResult?.status === 2 && getSavedFiltersApiResult?.data) {
      const savedFilters = getSavedFiltersApiResult.data
      const columnFilters = Array.isArray(savedFilters?.columnFilters)
        ? savedFilters.columnFilters.map((column: any) => ({
            ...column,
            value: Array.isArray(column?.value)
              ? column.value.map((val: any) => {
                  return Number(val) || val
                })
              : column.value
          }))
        : savedFilters
      setColumnFilters(columnFilters || [])
    }
  }, [getSavedFiltersApiResult])
  //#endregion

  const tableData = useMemo(
    () =>
      (pagedResult.status as TableLoadingStateEnum) === TableLoadingStateEnum.Loading
        ? Array(25).fill({})
        : (pagedResult.status as TableLoadingStateEnum) === TableLoadingStateEnum.Success && rows
          ? rows
          : [],
    [pagedResult.status as TableLoadingStateEnum, rows]
  )

  // export data for excel
  const exportData = useMemo(() => {
    if (nonPagedResult.status !== TableLoadingStateEnum.Success || !nonPagedResult.data) return []

    const issueReportsTable = tableId === ClpTableEnum.IssueReportsArchive
    const deviationsTable = tableId === ClpTableEnum.Deviations

    return nonPagedResult.data.map((row: any) => {
      const transformed = deviationsTable
        ? {
            ...row,
            status: deviationStatusStrings(row.status, t),
            priority: prio(row.priority, t),
            createdTime: row.createdTime ? moment(row.createdTime).format("YYYY-MM-DD HH:mm") : "",
            updatedTime: row.updatedTime ? moment(row.updatedTime).format("YYYY-MM-DD HH:mm") : ""
          }
        : issueReportsTable
          ? {
              ...row,
              status: statusString(row.status, t),
              priority: prio(row.priority, t),
              createdDate: row.createdDate ? moment(row.createdDate).format("YYYY-MM-DD HH:mm") : "",
              updatedDate: row.updatedDate ? moment(row.updatedDate).format("YYYY-MM-DD HH:mm") : ""
            }
          : row

      const rowArray: any[] = []
      columns.forEach((col) => {
        const key = (col as any).accessorKey || (col as any).id
        if (!key) {
          rowArray.push(null)
          return
        }

        const accessorFn = (col as any).accessorFn
        if (typeof accessorFn === "function") {
          rowArray.push(accessorFn(transformed))
        } else {
          rowArray.push(transformed[key])
        }
      })

      return rowArray
    })
  }, [nonPagedResult, columns, tableId, t])

  const table = useReactTable({
    defaultColumn: { meta: { filter: DefaultColumnFilter } },
    columns: tableColumns,
    data: tableData,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onPaginationChange: setPagination,
    pageCount: Math.ceil((pagedResult?.data?.totalCount ?? 0) / pagination.pageSize),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    state: {
      columnFilters: columnFilters,
      pagination,
      globalFilter
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize
      }
    }
  })

  const {
    getHeaderGroups,
    getRowModel,
    nextPage,
    previousPage,
    getCanNextPage,
    getCanPreviousPage,
    setPageIndex,
    getPageCount,
    getRowCount,
    getState
  } = table
  const history = useHistory()

  useEffect(() => {
    const handleLeave = () => {}
    const unlisten = history.listen((location, action) => {
      appContext.setTableState(() => {
        return {
          state: getState(),
          columns: columns.map((x) => x.header)
        }
      })
      if (action === "POP") {
        handleLeave()
      }
    })

    return () => {
      unlisten()
      handleLeave()
    }
  }, [history, getState()])

  const renderNoRows = () => {
    return (
      <NoResultFound
        icon={faLayerGroup}
        message={t("global.table.no_results_found", "No results found")}
        backgroundColor={(theme.palette as any).primary[300]}
        color={(theme.palette as any).primary[800]}
        size="lg"
      />
    )
  }

  const renderFooter = () => (
    <StyledTableFooter>
      {footer && footer.length > 0 && (
        <TableRow>
          <TableCell>{t("global.table.total", "Total")}:</TableCell>
          {footer.map((item: any) => (
            <TableCell key={item}>
              {item.value > 0 && parseFloat(item.value).toFixed(2).replace(".", ",")}
            </TableCell>
          ))}
        </TableRow>
      )}
    </StyledTableFooter>
  )

  const renderPagination = () => (
    <Pagination
      pageIndex={pagination.pageIndex + 1}
      gotoPage={setPageIndex}
      previousPage={previousPage}
      canPreviousPage={getCanPreviousPage}
      nextPage={nextPage}
      canNextPage={getCanNextPage}
      pageCount={getPageCount}
    />
  )

  return (
    <>
      <StyledFilterContainer>
        <Box>
          {rows?.length > 0 && !hideSearch && (
            <Grid display="flex" item>
              <Typography variant="h6" mb={1}>
                {t("global.table.filter", "Filter")}:
              </Typography>
            </Grid>
          )}
          <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid xs={isPhone && 12} item>
              <StyledFilterGrid gap={2}>
                {!hideSearch && setGlobalFilter && (
                  <GlobalFilter initialValue={globalFilter} setGlobalFilter={setGlobalFilter} />
                )}
                {getHeaderGroups()?.map((headerGroup) =>
                  headerGroup.headers
                    .filter(
                      (x) =>
                        !x.column.columnDef.meta?.hiddenFilter &&
                        x.column.columnDef.meta?.mainFilter &&
                        x.column.columnDef.header
                    )
                    .map((header, index) => {
                      return (
                        <Grid item key={index}>
                          {flexRender(header.column.columnDef?.meta?.filter ?? DefaultColumnFilter, {
                            column: header.column
                          })}
                        </Grid>
                      )
                    })
                )}
              </StyledFilterGrid>
            </Grid>
            <Grid item>
              <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                {!hideFilter && (
                  <Grid item>
                    <Button
                      color="primary"
                      startIcon={<FilterList />}
                      onClick={() => expandFilters(!filtersOpen)}>
                      {filtersOpen
                        ? t("global.table.hide_filter", "Hide filter")
                        : t("global.table.show_all_filter", "Show all filters")}
                    </Button>
                  </Grid>
                )}
                {tableId != null && (
                  <Grid item>
                    <DefaultButton
                      variant="text"
                      startIcon={<Save />}
                      result={saveFiltersApiResult}
                      onClick={() => saveFilters()}>
                      {t("global.table.save_filter", "Save filter")}
                    </DefaultButton>
                    {saveFilterTooltipText && (
                      <Tooltip title={saveFilterTooltipText}>
                        <IconButton size="small" sx={{ color: (theme.palette as any).primary[700] }}>
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                )}
                <Grid item>
                  <ExportToExcel columns={columns} rows={exportData} />
                  {print && <PrintTableToPdt columns={columns} rows={table.getFilteredRowModel().rows} />}
                </Grid>
                <Grid item>
                  <RadioButtons
                    pageSize={pagination.pageSize}
                    setPageSize={(size: number) => {
                      setPagination((prev) => ({ ...prev, pageSize: size, pageIndex: 0 }))
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Grid container direction="column" justifyContent="space-between">
          <Grid item>
            <SelectedFilters table={table} setFiltering={setColumnFilters} />
          </Grid>
          {filtersOpen && (
            <Grid item>
              <MoreFilters rows={getRowModel()?.rows} headerGroups={getHeaderGroups()} />
            </Grid>
          )}
        </Grid>
      </StyledFilterContainer>
      <Box marginX={2} mb={1} display="flex" justifyContent="space-between" alignItems="center">
        {(pagedResult.status as TableLoadingStateEnum) === TableLoadingStateEnum.Success &&
          (rows && rows.length > 0 ? (
            <Grid container direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Grid>
                <Typography>
                  {t("global.table.total", "Total")}: {pagedResult.data?.totalCount}{" "}
                  {t("global.rows", "Rows")}
                </Typography>
              </Grid>
            </Grid>
          ) : null)}
        <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
          <Grid item>{renderPagination()} </Grid>
        </Grid>
      </Box>
      <StyledBox>
        <StyledTable size="small">
          {getHeaderGroups().map((headerGroup) => (
            <TableHead key={headerGroup.id} sx={{ display: { xs: "none", md: "table-header-group" } }}>
              <TableRow>
                {subColumns && subColumns.length > 0 && <TableCell key="subColumnHeader" />}
                {headerGroup.headers.map((header, index) => {
                  return header.isPlaceholder ? null : (
                    <TableHeader key={header.id} header={header} setSort={setSort} currentSort={sort} />
                  )
                })}
              </TableRow>
            </TableHead>
          ))}
          <TableBody>
            {getRowModel()?.rows?.map((row, i) => {
              return (
                <ClpTableRow
                  columns={columns}
                  key={i}
                  row={row}
                  subColumns={subColumns}
                  onRowClick={onRowClick}
                />
              )
            })}
          </TableBody>

          {renderFooter()}
        </StyledTable>
        {(pagedResult.status as TableLoadingStateEnum) === TableLoadingStateEnum.Success &&
          (rows && rows.length > 0 ? (
            <Grid container direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Grid pl={2} py={1}>
                <Typography>
                  {t("global.table.total", "Total")}: {pagedResult.data?.totalCount}{" "}
                  {t("global.rows", "Rows")}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            renderNoRows()
          ))}
      </StyledBox>
    </>
  )
}

export default ClpTableServerSidePaging
